import { effect, inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Browser timezone sync: once per app load (per authenticated session),
 * reads the IANA tz name from `Intl` and sends it to the backend. The
 * backend keeps `users.timezone` FOLLOWING the browser - travel somewhere
 * and the next app load moves the DDR's clock with you, and it snaps back
 * when you get home. A manually pinned tz (timezone_source = 'manual') is
 * never auto-overwritten.
 *
 * Phase 2 (flagged separately): mismatch banner + per-session override +
 * settings UI.
 */
@Injectable({ providedIn: 'root' })
export class TimezoneDetectionService {
    private readonly authService = inject(AuthService);
    private readonly http = inject(HttpClient);
    private succeeded = false;
    private inFlight = false;
    private attempts = 0;

    // noinspection JSUnusedLocalSymbols
    private readonly _detectOnLogin = effect(() => {
        const user = this.authService.state().user;
        if (!user || this.succeeded || this.inFlight || this.authService.isGuest()) {
            return;
        }
        const tz = this.detectBrowserTimezone();
        if (!tz) {
            return;
        }
        void this.reportWithRetry(tz);
    });

    private detectBrowserTimezone(): string | null {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
        } catch {
            return null;
        }
    }

    /** Send the tz, retrying on failure (10s, then 60s). The old version
     *  marked itself done BEFORE the request, so one failed PUT on a slow
     *  login left users.timezone empty until the next full app load — one of
     *  the "everything shows UTC" causes. Success is the only thing that
     *  stops the effect from re-arming. */
    private async reportWithRetry(tz: string): Promise<void> {
        this.inFlight = true;
        try {
            const body = new FormData();
            body.set('tz', tz);
            await firstValueFrom(this.http.put(`${environment.apiBase}/users/me/timezone`, body));
            this.succeeded = true;
        } catch {
            this.attempts += 1;
            if (this.attempts <= 2) {
                const delay = this.attempts === 1 ? 10_000 : 60_000;
                setTimeout(() => {
                    this.inFlight = false;
                    const user = this.authService.state().user;
                    if (user && !this.succeeded && !this.authService.isGuest()) {
                        void this.reportWithRetry(this.detectBrowserTimezone() ?? tz);
                    }
                }, delay);
                return;
            }
            // Out of retries this app load; the per-request X-Browser-Timezone
            // header sync is the remaining backstop.
        } finally {
            if (this.attempts <= 2 || this.succeeded) {
                // retry path manages inFlight in its timer
            } else {
                this.inFlight = false;
            }
        }
    }
}
